import { useMemo, useState } from 'react';
import type { MoneyMonth, PersonalBill, PersonalIncome } from '../../../lib/api/team.js';
import { hapticTap } from '../../../lib/native.js';
import { money } from './Ganhos.js';

/**
 * O Dinheiro em desenho (13/10, pedido do Igor: "mais refinado e visual, como o painel do
 * dono"). Tudo calculado por código a partir do que a pessoa anotou; nada de conselho nem
 * de banco. Quatro peças: o mês em anéis, os últimos meses em colunas, os próximos 30 dias
 * numa linha e como as contas em aberto se dividem.
 */

const monthShort = (month: string) => {
  const [year, index] = month.split('-').map(Number) as [number, number];
  return new Intl.DateTimeFormat('pt-BR', { month: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, index - 1, 15)))
    .replace('.', '');
};
const compact = (cents: number) => {
  const reais = Math.abs(cents) / 100;
  const sign = cents < 0 ? '-' : '';
  if (reais >= 1000) return `${sign}R$ ${(reais / 1000).toFixed(1).replace('.', ',')} mil`;
  return `${sign}R$ ${Math.round(reais)}`;
};

/** Arco de um anel (0 a 1) a partir de `start` (0 a 1), em volta do centro. */
function Arc({
  r,
  from,
  to,
  className,
}: {
  r: number;
  from: number;
  to: number;
  className: string;
}) {
  const C = 2 * Math.PI * r;
  const len = Math.max(0, Math.min(1, to) - Math.max(0, from)) * C;
  if (len < 0.5) return null;
  return (
    <circle
      className={className}
      cx="60"
      cy="60"
      r={r}
      strokeDasharray={`${len} ${C - len}`}
      strokeDashoffset={-from * C}
    />
  );
}

/** O mês: anel de fora = entradas (entrou / para cair); de dentro = saídas (pago / a pagar). */
export function MoneyHero({ month }: { month: MoneyMonth }) {
  const inTotal = month.receivedCents + month.expectedCents;
  const outTotal = month.paidCents + month.dueCents;
  const scale = Math.max(1, inTotal, outTotal);
  const short = month.leftoverCents < 0;
  return (
    <div className="dv-hero" role="group" aria-label="O mês em números">
      <div className="dv-rings" aria-hidden="true">
        <svg viewBox="0 0 120 120">
          <circle className="dv-track" cx="60" cy="60" r="52" />
          <circle className="dv-track" cx="60" cy="60" r="38" />
          <Arc r={52} from={0} to={month.receivedCents / scale} className="dv-arc in" />
          <Arc
            r={52}
            from={month.receivedCents / scale}
            to={inTotal / scale}
            className="dv-arc in soft"
          />
          <Arc r={38} from={0} to={month.paidCents / scale} className="dv-arc out" />
          <Arc
            r={38}
            from={month.paidCents / scale}
            to={outTotal / scale}
            className="dv-arc out soft"
          />
        </svg>
        <span className={`dv-center${short ? ' short' : ''}`}>
          <small>{short ? 'Falta' : 'Sobra'}</small>
          <b>{compact(Math.abs(month.leftoverCents))}</b>
        </span>
      </div>
      <dl className="dv-legend">
        <div className="in">
          <dt>Entrou</dt>
          <dd>{money(month.receivedCents)}</dd>
        </div>
        <div className="in soft">
          <dt>Para cair</dt>
          <dd>{money(month.expectedCents)}</dd>
        </div>
        <div className="out">
          <dt>Pago</dt>
          <dd>{money(month.paidCents)}</dd>
        </div>
        <div className="out soft">
          <dt>A pagar</dt>
          <dd>{money(month.dueCents)}</dd>
        </div>
      </dl>
      <p className={`dv-foot${short ? ' short' : ''}`}>
        {short
          ? `Se tudo cair e for pago, faltam ${money(-month.leftoverCents)} para fechar o mês.`
          : `Se tudo cair e for pago, sobram ${money(month.leftoverCents)}.`}
        {month.savingsRate !== null && month.savingsRate > 0 && !short
          ? ` É ${month.savingsRate}% do que entra.`
          : ''}
      </p>
    </div>
  );
}

/** Os últimos meses: entrou e saiu em colunas, a sobra em pontos; tocar escolhe o mês. */
export function MonthsChart({
  history,
  onMore,
}: {
  history: MoneyMonth[];
  onMore?: (() => void) | undefined;
}) {
  const months = useMemo(() => {
    const moved = history.filter(
      (item) => item.receivedCents + item.expectedCents + item.paidCents + item.dueCents > 0,
    );
    return (moved.length ? history.slice(history.indexOf(moved[0]!)) : history).slice(-6);
  }, [history]);
  const [picked, setPicked] = useState(months.length - 1);
  if (months.length < 2) return null;
  const W = 300;
  const H = 120;
  const top = 10;
  const base = 96;
  const inner = base - top;
  const step = W / months.length;
  const max = Math.max(
    1,
    ...months.map((item) =>
      Math.max(item.receivedCents + item.expectedCents, item.paidCents + item.dueCents),
    ),
  );
  const y = (cents: number) => base - (Math.max(0, cents) / max) * inner;
  const chosen = months[picked] ?? months.at(-1)!;
  const last = months.length - 1;
  const left = (item: MoneyMonth, index: number) =>
    index === last ? item.leftoverCents : item.receivedCents - item.paidCents;
  return (
    <section className="pgs-section dv-card">
      <div className="dv-card-head">
        <h2 className="pgs-h2">Seus últimos meses</h2>
        {onMore && (
          <button type="button" className="dv-more" onClick={onMore}>
            Mês a mês e planilha ›
          </button>
        )}
      </div>
      <div className="dv-chosen" aria-live="polite">
        <b>{monthShort(chosen.month)}</b>
        <span className="in">entrou {money(chosen.receivedCents)}</span>
        <span className="out">saiu {money(chosen.paidCents)}</span>
        <span className={left(chosen, picked) < 0 ? 'short' : 'left'}>
          {picked === last ? 'sobra prevista' : 'sobrou'} {money(left(chosen, picked))}
        </span>
      </div>
      <svg
        className="dv-chart"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Entrou e saiu nos últimos meses"
      >
        <line className="dv-base" x1="0" x2={W} y1={base} y2={base} />
        {months.map((item, index) => {
          const cx = step * index + step / 2;
          const bw = Math.min(16, step * 0.26);
          const inTop = y(item.receivedCents + (index === last ? item.expectedCents : 0));
          const outTop = y(item.paidCents + (index === last ? item.dueCents : 0));
          return (
            <g
              key={item.month}
              className={index === picked ? 'on' : undefined}
              onClick={() => {
                setPicked(index);
                void hapticTap();
              }}
            >
              <rect className="dv-hit" x={cx - step / 2} y="0" width={step} height={H} />
              <rect
                className={`dv-col in${index === last ? ' now' : ''}`}
                x={cx - bw - 1.5}
                y={inTop}
                width={bw}
                height={base - inTop}
                rx="3"
              />
              <rect
                className={`dv-col out${index === last ? ' now' : ''}`}
                x={cx + 1.5}
                y={outTop}
                width={bw}
                height={base - outTop}
                rx="3"
              />
              <circle
                className={`dv-dot${left(item, index) < 0 ? ' short' : ''}`}
                cx={cx}
                cy={y(Math.abs(left(item, index)))}
                r={index === picked ? 4 : 3}
              />
              <text className="dv-tick" x={cx} y={H - 6} textAnchor="middle">
                {monthShort(item.month)}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="dv-key" aria-hidden="true">
        <span className="in">
          <i /> Entrou
        </span>
        <span className="out">
          <i /> Saiu
        </span>
        <span className="left">
          <i /> Sobra
        </span>
      </div>
    </section>
  );
}

const DAY = 86_400_000;
const spToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
const dayShort = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  });

/** Os próximos 30 dias numa linha: contas acima (laranja), ganhos abaixo (verde). */
export function NextDays({ bills, incomes }: { bills: PersonalBill[]; incomes: PersonalIncome[] }) {
  const today = spToday();
  const end = new Date(Date.parse(`${today}T12:00:00Z`) + 30 * DAY).toISOString().slice(0, 10);
  const marks = [
    ...bills
      .filter((bill) => bill.status === 'open' && bill.dueDate <= end)
      .map((bill) => ({
        id: bill.id,
        kind: 'out' as const,
        date: bill.dueDate < today ? today : bill.dueDate,
        late: bill.dueDate < today,
        title: bill.title,
        cents: bill.amountCents,
      })),
    ...incomes
      .filter((item) => item.status === 'expected' && item.date <= end)
      .map((item) => ({
        id: item.id,
        kind: 'in' as const,
        date: item.date < today ? today : item.date,
        late: item.date < today,
        title: item.title,
        cents: item.amountCents,
      })),
  ].sort((a, b) => a.date.localeCompare(b.date));
  const [picked, setPicked] = useState<string | null>(null);
  if (!marks.length) return null;
  const pos = (date: string) =>
    Math.min(
      1,
      Math.max(
        0,
        (Date.parse(`${date}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / (30 * DAY),
      ),
    );
  const chosen = marks.find((mark) => mark.id === picked) ?? null;
  const outSum = marks
    .filter((mark) => mark.kind === 'out')
    .reduce((sum, mark) => sum + mark.cents, 0);
  const inSum = marks
    .filter((mark) => mark.kind === 'in')
    .reduce((sum, mark) => sum + mark.cents, 0);
  return (
    <section className="pgs-section dv-card">
      <div className="dv-card-head">
        <h2 className="pgs-h2">Os próximos 30 dias</h2>
        <small className="dv-sums">
          <span className="out">{money(outSum)} a pagar</span>
          {inSum > 0 && <span className="in">{money(inSum)} para cair</span>}
        </small>
      </div>
      <div className="dv-line" role="list" aria-label="Contas e ganhos dos próximos 30 dias">
        <span className="dv-axis" aria-hidden="true" />
        <span className="dv-today" aria-hidden="true">
          hoje
        </span>
        <span className="dv-end" aria-hidden="true">
          {dayShort(end)}
        </span>
        {marks.map((mark) => (
          <button
            key={mark.id}
            type="button"
            role="listitem"
            className={`dv-mark ${mark.kind}${mark.late ? ' late' : ''}${picked === mark.id ? ' on' : ''}`}
            style={{ left: `${pos(mark.date) * 100}%` }}
            aria-label={`${mark.title}, ${money(mark.cents)}, ${mark.late ? 'atrasado' : dayShort(mark.date)}`}
            onClick={() => {
              setPicked(picked === mark.id ? null : mark.id);
              void hapticTap();
            }}
          />
        ))}
      </div>
      <p className="dv-mark-note" aria-live="polite">
        {chosen
          ? `${chosen.title} · ${money(chosen.cents)} · ${chosen.late ? 'já passou do dia' : dayShort(chosen.date)}`
          : 'Toque num ponto para ver o que é.'}
      </p>
    </section>
  );
}

/** Como as contas em aberto se dividem, numa rosca. */
export function SplitDonut({ groups }: { groups: { label: string; cents: number }[] }) {
  const total = groups.reduce((sum, group) => sum + group.cents, 0);
  if (groups.length < 2 || total <= 0) return null;
  const R = 34;
  const C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <section className="pgs-section dv-card">
      <h2 className="pgs-h2">
        Como se divide <small>em aberto</small>
      </h2>
      <div className="dv-split">
        <svg viewBox="0 0 90 90" aria-hidden="true">
          <circle className="dv-track" cx="45" cy="45" r={R} />
          {groups.map((group, index) => {
            const len = (group.cents / total) * C;
            const el = (
              <circle
                key={group.label}
                className={`dv-slice s${index % 6}`}
                cx="45"
                cy="45"
                r={R}
                strokeDasharray={`${Math.max(0, len - 1.5)} ${C - Math.max(0, len - 1.5)}`}
                strokeDashoffset={-acc}
              />
            );
            acc += len;
            return el;
          })}
        </svg>
        <ul>
          {groups.map((group, index) => (
            <li key={group.label}>
              <i className={`s${index % 6}`} />
              <span>{group.label}</span>
              <b>{money(group.cents)}</b>
              <small>{Math.round((group.cents / total) * 100)}%</small>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
